import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useLang, useTheme, usePerm } from "../lib/providers";
import {
  SquaresFour, FileText, Receipt, Stack, Tag, UsersThree, SignOut, Translate, Gear, Sun, Moon, UserGear, List as ListIcon, X as XIcon, Ruler, Files,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/dashboard",  key: "dashboard",  Icon: SquaresFour, testid: "nav-dashboard",  permKey: "dashboard"  },
  { to: "/quotes",     key: "quotes",     Icon: FileText,    testid: "nav-quotes",     permKey: "quotes"     },
  { to: "/aufmasse",   key: "aufmasse",   Icon: Ruler,       testid: "nav-aufmasse",   permKey: "aufmasse"   },
  { to: "/invoices",   key: "invoices",   Icon: Receipt,     testid: "nav-invoices",   permKey: "invoices"   },
  { to: "/pdf-bundle", key: "pdfBundle",  Icon: Files,       testid: "nav-pdf-bundle", permKey: null         },
  { to: "/positions",  key: "positions",  Icon: Stack,       testid: "nav-positions",  permKey: "positions"  },
  { to: "/categories", key: "categories", Icon: Tag,         testid: "nav-categories", permKey: "categories" },
  { to: "/customers",  key: "customers",  Icon: UsersThree,  testid: "nav-customers",  permKey: "customers"  },
  { to: "/users",      key: "users",      Icon: UserGear,    testid: "nav-users",      permKey: "users"      },
  { to: "/settings",   key: "settings",   Icon: Gear,        testid: "nav-settings",   permKey: "settings"   },
];

const ROLE_LABELS = {
  de: { admin: "Admin", expert: "Experte", user: "Anwender", handworker: "Handwerker" },
  en: { admin: "Admin", expert: "Expert", user: "User", handworker: "Handworker" },
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const { tabHidden } = usePerm();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const visibleItems = navItems.filter((item) => !tabHidden[item.permKey]);
  const roleLabel = ROLE_LABELS[lang]?.[user?.role] || user?.role || "";

  const SidebarBody = (
    <>
      <div className="px-5 py-6 border-b border-[var(--border)] flex items-center gap-2">
        <div className="w-7 h-7 bg-[var(--brand)] flex items-center justify-center">
          <Receipt weight="fill" size={16} color="white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-base leading-tight">FalkeDeck</div>
          <div className="label-overline leading-none mt-0.5">Craft Smarter. Bill Faster.</div>
        </div>
        <button
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden text-[var(--muted)] hover:text-[var(--ink)] p-1"
          data-testid="sidebar-close"
          aria-label="Close menu"
        >
          <XIcon size={20} />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {visibleItems.map(({ to, key, Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors duration-150 ${
                isActive
                  ? "bg-[var(--brand)] text-white"
                  : "text-[var(--muted)] hover:bg-[var(--row-hover)] hover:text-[var(--ink)]"
              }`
            }
          >
            <Icon size={18} weight="regular" />
            <span>{t(key)}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-[var(--border)] space-y-1">
        <div className="px-3 py-2">
          <div className="label-overline" data-testid="user-role-label">{roleLabel}</div>
          <div className="text-sm font-medium truncate text-[var(--ink)]">{user?.name || user?.email}</div>
          <div className="text-xs text-[var(--muted-2)] truncate">{user?.email}</div>
        </div>
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--muted)] hover:bg-[var(--row-hover)] hover:text-[var(--ink)] rounded-sm transition-colors"
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          <span>{theme === "dark" ? t("lightMode") : t("darkMode")}</span>
        </button>
        <button
          onClick={toggleLang}
          data-testid="lang-switch"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--muted)] hover:bg-[var(--row-hover)] hover:text-[var(--ink)] rounded-sm transition-colors"
        >
          <Translate size={18} />
          <span>{lang.toUpperCase()}</span>
          <span className="text-[var(--muted-2)] ml-auto">{lang === "de" ? "→ EN" : "→ DE"}</span>
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--muted)] hover:bg-[var(--row-hover)] hover:text-[var(--ink)] rounded-sm transition-colors"
        >
          <SignOut size={18} />
          <span>{t("logout")}</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[var(--bg)] text-[var(--ink)]">
      {/* Mobile/tablet top bar (shown below lg) */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-[var(--surface)] border-b border-[var(--border)] flex items-center px-4 gap-3">
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="sidebar-open"
          className="text-[var(--muted)] hover:text-[var(--ink)] p-1 -ml-1"
          aria-label="Open menu"
        >
          <ListIcon size={22} />
        </button>
        <div className="w-6 h-6 bg-[var(--brand)] flex items-center justify-center">
          <Receipt weight="fill" size={13} color="white" />
        </div>
        <div className="font-heading font-semibold text-sm">FalkeDeck</div>
      </div>

      {/* Drawer overlay */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar — fixed on mobile (drawer), static on desktop */}
      <aside
        className={`fixed lg:static top-0 left-0 z-50 h-screen lg:h-auto w-72 lg:w-60 shrink-0 border-r border-[var(--border)] bg-[var(--surface)] flex flex-col transition-transform duration-200 ${
          drawerOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        {SidebarBody}
      </aside>

      <main className="flex-1 min-w-0 overflow-x-clip pt-14 lg:pt-0">{children}</main>
    </div>
  );
}
